import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, CreditCard, Mail, Type, ArrowRight, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STEPS = [
  { id: "welcome", label: "Welcome" },
  { id: "admin", label: "Admin Password" },
  { id: "branding", label: "App Name" },
  { id: "payments", label: "Payments" },
  { id: "marketing", label: "Marketing" },
  { id: "done", label: "Done" },
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    admin_password: "",
    confirm_password: "",
    app_name: "Labelled",
    stripe_api_key: "",
    klaviyo_api_key: "",
  });

  const update = (field, value) => setForm({ ...form, [field]: value });

  const handleComplete = async () => {
    if (form.admin_password && form.admin_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.admin_password && form.admin_password !== form.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/setup/complete`, {
        admin_password: form.admin_password || undefined,
        app_name: form.app_name || undefined,
        stripe_api_key: form.stripe_api_key || undefined,
        klaviyo_api_key: form.klaviyo_api_key || undefined,
      });
      setStep(STEPS.length - 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    }
    setSaving(false);
  };

  const next = () => {
    if (step === STEPS.length - 2) {
      handleComplete();
    } else {
      setStep(step + 1);
    }
  };

  const canSkip = step > 0 && step < STEPS.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 flex items-center justify-center p-4" data-testid="setup-wizard">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center justify-center gap-1.5 mb-8">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`h-1.5 rounded-full transition-all ${
                i <= step ? "bg-indigo-500 w-8" : "bg-slate-200 w-4"
              }`}
            />
          ))}
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8">
          {/* Step: Welcome */}
          {step === 0 && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto">
                <Sparkles className="w-8 h-8 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: "Nunito" }}>
                Welcome to Labelled
              </h1>
              <p className="text-slate-500 leading-relaxed">
                Let's get your community map set up. This takes about 2 minutes. You can change everything later from the admin dashboard.
              </p>
            </div>
          )}

          {/* Step: Admin Password */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>Set your admin password</h2>
                  <p className="text-sm text-slate-500">You'll use this to log in at /admin</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={form.admin_password}
                  onChange={(e) => update("admin_password", e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  data-testid="setup-password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => update("confirm_password", e.target.value)}
                  placeholder="Type it again"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  data-testid="setup-password-confirm"
                />
              </div>
              <p className="text-xs text-slate-400">Username is always <strong>admin</strong>. Skip to keep the default password.</p>
            </div>
          )}

          {/* Step: App Name */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Type className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>Name your community</h2>
                  <p className="text-sm text-slate-500">This appears in the header and browser tab</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">App Name</label>
                <input
                  type="text"
                  value={form.app_name}
                  onChange={(e) => update("app_name", e.target.value)}
                  placeholder="e.g. Labelled, ND Connect"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                  data-testid="setup-app-name"
                />
              </div>
              <p className="text-xs text-slate-400">You can upload a custom logo later from the admin dashboard.</p>
            </div>
          )}

          {/* Step: Payments */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>Accept payments (optional)</h2>
                  <p className="text-sm text-slate-500">For featured event listings</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Stripe Secret Key</label>
                <input
                  type="password"
                  value={form.stripe_api_key}
                  onChange={(e) => update("stripe_api_key", e.target.value)}
                  placeholder="sk_live_xxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  data-testid="setup-stripe-key"
                />
              </div>
              <a
                href="https://dashboard.stripe.com/apikeys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Get your Stripe key here <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <p className="text-xs text-slate-400">Skip if you don't want paid event listings yet. You can add this later in admin settings.</p>
            </div>
          )}

          {/* Step: Marketing */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>Email collection (optional)</h2>
                  <p className="text-sm text-slate-500">Sync new subscribers to Klaviyo</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Klaviyo API Key</label>
                <input
                  type="password"
                  value={form.klaviyo_api_key}
                  onChange={(e) => update("klaviyo_api_key", e.target.value)}
                  placeholder="pk_xxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                  data-testid="setup-klaviyo-key"
                />
              </div>
              <a
                href="https://www.klaviyo.com/settings/account/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Get your Klaviyo key here <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <p className="text-xs text-slate-400">Emails are still collected without Klaviyo — you can always download them as CSV from admin. Skip if not needed.</p>
            </div>
          )}

          {/* Step: Done */}
          {step === STEPS.length - 1 && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800" style={{ fontFamily: "Nunito" }}>You're all set!</h2>
              <p className="text-slate-500 leading-relaxed">
                Your community map is ready. You can manage everything from the <strong>admin dashboard</strong> — change settings, manage labels, moderate events, and view analytics.
              </p>
              <div className="bg-slate-50 rounded-xl p-4 text-left text-sm space-y-2">
                <p className="font-semibold text-slate-700">Quick links:</p>
                <p className="text-slate-600">Map: <strong>/</strong> (your homepage)</p>
                <p className="text-slate-600">Admin: <strong>/admin</strong> (username: admin)</p>
                <p className="text-slate-600">Host Portal: <strong>/host</strong> (for event hosts)</p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8">
            {step === STEPS.length - 1 ? (
              <button
                onClick={() => navigate("/")}
                className="w-full btn-primary py-3 text-base"
                data-testid="setup-go-to-map"
              >
                Go to your map <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                {canSkip && (
                  <button
                    onClick={next}
                    className="text-sm text-slate-400 hover:text-slate-600 font-medium"
                    data-testid="setup-skip"
                  >
                    Skip
                  </button>
                )}
                <button
                  onClick={next}
                  disabled={saving}
                  className="ml-auto btn-primary py-3 px-8"
                  data-testid="setup-next"
                >
                  {saving ? "Saving..." : step === STEPS.length - 2 ? "Finish Setup" : step === 0 ? "Let's go" : "Next"}
                  {!saving && <ArrowRight className="w-4 h-4" />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
